import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { PaginationParams, SortingParams } from "@src/types/httpApi";
import { IMood, IMoodsFilters } from "@src/types/mood";
import { ITag } from "@src/types/tags";
import { ILibraryFilterForm } from "@src/redux/slices/librarySlice";

type CompleteFilterType = Partial<IMoodsFilters> & {
  sorting: SortingParams;
} & { pagination: PaginationParams };

/**
 * Library-style track filters used by the Mood +Add panel. Tags and moods are
 * intentionally excluded here because the panel manages those through
 * `tracksTagFilter` / `moodsFilter`.
 */
export type MoodTrackLibraryFilters = Omit<
  ILibraryFilterForm,
  "includedTags" | "excludedTags" | "includedMoods" | "excludedMoods"
>;

export const moodTrackLibraryFiltersInitialState: MoodTrackLibraryFilters = {
  title: "",
  artist: "",
  album: "",
  label: "",
  bpm: 0,
  key: "",
  durationInMinutes: 0,
  isTopLikedThisWeek: false,
  missingYoutube: false,
  missingSpotify: false,
  missingApple: false,
  missingShazam: false,
  createdDateRange: undefined,
};
interface MoodFactoryState {
  filters: CompleteFilterType;
  sort: {
    field: string;
    direction: "asc" | "desc";
  };
  creatingMood: {
    isSuccess: boolean;
    isLoading: boolean;
    error: null | string;
  };
  totalMoods: number;
  spaceUsed: number;

  tracksTagFilter: {
    tags: ITag[];
    excludedTags: ITag[];
  };
  moodsFilter: {
    moods: IMood[];
    excludedMoods: IMood[];
  };
  searchTracksText: string;
  trackLibraryFilters: MoodTrackLibraryFilters;
}

export const moodFactoryInitialState: MoodFactoryState = {
  filters: {
    color: "",
    platform: "",
    description: "",
    title: "",
    searchText: "",
    sorting: {
      field: "createdAt",
      direction: "asc",
    },
    pagination: {
      page: 1,
      pageSize: 50,
    },
  },
  creatingMood: {
    isSuccess: false,
    isLoading: false,
    error: null,
  },
  sort: {
    field: "updatedAt",
    direction: "desc",
  },
  totalMoods: 0,
  spaceUsed: 0,

  tracksTagFilter: {
    tags: [],
    excludedTags: [],
  },
  moodsFilter: {
    moods: [],
    excludedMoods: [],
  },
  searchTracksText: "",
  trackLibraryFilters: { ...moodTrackLibraryFiltersInitialState },
};

const playlistSlice = createSlice({
  name: "moodFactorySlice",
  initialState: moodFactoryInitialState,
  reducers: {
    setFilters(state, action: PayloadAction<Partial<CompleteFilterType>>) {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
    },
    resetFilters(state) {
      state.filters = {
        ...moodFactoryInitialState.filters,
      };
    },
    setSort(
      state,
      action: PayloadAction<{ field: string; direction: "asc" | "desc" }>
    ) {
      state.sort = action.payload;
    },
    resetSort(state) {
      state.sort = moodFactoryInitialState.sort;
    },
    setMoodsCount(state, action: PayloadAction<number>) {
      state.totalMoods = action.payload;
    },
    setMoodsSpaceUsed(state, action: PayloadAction<number>) {
      state.spaceUsed = action.payload;
    },

    setTracksTagFilter(
      state,
      action: PayloadAction<{ tags: ITag[]; excludedTags: ITag[] }>
    ) {
      state.tracksTagFilter.tags = action.payload.tags || [];
      state.tracksTagFilter.excludedTags = action.payload.excludedTags || [];
    },
    setMoodsFilter(
      state,
      action: PayloadAction<{ moods: IMood[]; excludedMoods: IMood[] }>
    ) {
      state.moodsFilter.moods = action.payload.moods || [];
      state.moodsFilter.excludedMoods = action.payload.excludedMoods || [];
    },
    setSearchTracksText(state, action: PayloadAction<string>) {
      state.searchTracksText = action.payload;
    },
    setTrackLibraryFilters(
      state,
      action: PayloadAction<Partial<MoodTrackLibraryFilters>>
    ) {
      state.trackLibraryFilters = {
        ...state.trackLibraryFilters,
        ...action.payload,
      };
    },
    resetTrackLibraryFilters(state) {
      state.trackLibraryFilters = { ...moodTrackLibraryFiltersInitialState };
    },
  },
});

export const {
  setMoodsCount,
  setMoodsSpaceUsed,
  setFilters,
  resetFilters,
  setSort,
  resetSort,
  setTracksTagFilter,
  setMoodsFilter,
  setSearchTracksText,
  setTrackLibraryFilters,
  resetTrackLibraryFilters,
} = playlistSlice.actions;

export default playlistSlice.reducer;
